import { useStore } from '@nanostores/solid'
import { QueryClientProvider, useIsMutating } from '@tanstack/solid-query'
import { createResource, createSignal, Suspense } from 'solid-js'
import { CloseButton } from '~/components/close-button'
import { CountdownSection } from '~/components/countdown-section'
import { Dialog } from '~/components/dialog'
import { FixedSubscribeButton } from '~/components/fixed-subscribe-button'
import { FooterDescription } from '~/components/footer-description'
import { Hero } from '~/components/hero'
import { KvTop } from '~/components/kv-top'
import { LoadingOverlay } from '~/components/loading-overlay'
import { PlanSelect } from '~/components/plan-select'
import { ReminderDialog } from '~/components/reminder-dialog'
import { ReminderView } from '~/components/reminder-view'
import { SubscribedView } from '~/components/subscribed-view'
import { useChinaAndroidTrialMutation } from '~/hooks/use-china-android-trial-mutation'
import { useDeviceClockGuard } from '~/hooks/use-device-clock-guard'
import { useSubscribeMutation } from '~/hooks/use-subscribe-mutation'
import { useTimestamp } from '~/hooks/use-timestamp'
import { productsReady } from '~/lib/message'
import { platform } from '~/lib/params'
import { queryClient } from '~/lib/query-client'
import { selectPlanVariant } from '~/lib/select-plan-variant'
import {
  $chinaAndroidTrialEligibility,
  $selectedPlan,
  $subscribedView,
} from '~/store'

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense>
        <div class="relative flex min-h-screen flex-col items-center overflow-clip bg-gradient-to-t from-[#060e0c] from-[15%] to-[#236e47] to-[90%] pb-56 pt-[var(--safe-area-top)]">
          <KvTop />
          <CloseButton />
          <main class="relative flex w-full max-w-[640px] flex-col items-center gap-6 px-8 pt-8">
            <Hero />
            <CountdownSection />
            <PlanSelect />
            <FooterDescription />
          </main>
          <FixedSubscribeButton />
          <ReminderDialog />
          <ReminderHost />
          <SubscribedHost />
          <LoadingHost />
        </div>
      </Suspense>

      <AppBody />
      <Dialog />
    </QueryClientProvider>
  )
}

function AppBody() {
  useDeviceClockGuard()
  useTimestamp()
  return null
}

function LoadingHost() {
  const isMutating = useIsMutating()
  return <LoadingOverlay isOpen={isMutating() > 0} />
}

function ReminderHost() {
  const [products] = createResource(() => productsReady.promise)
  const chinaTrial = useStore($chinaAndroidTrialEligibility)
  const selectedPlan = useStore($selectedPlan)

  const subscribeMutation = useSubscribeMutation()
  const chinaAndroidTrialMutation = useChinaAndroidTrialMutation()

  const variant = () =>
    selectPlanVariant({
      platform,
      products: products() ?? null,
      chinaAndroidTrialEligibility: chinaTrial(),
    })

  const startTrial = () => {
    const v = variant()
    if (v.kind === 'loading' || v.kind === 'error') return
    if (v.kind === 'china-trial-cta') {
      chinaAndroidTrialMutation.mutate()
      return
    }
    const key = selectedPlan() === 'annual' ? 'annualEarlyBird' : v.right
    const product = products()?.[key]
    if (!product) return
    subscribeMutation.mutate({ product })
  }

  const isLoading = () =>
    subscribeMutation.isPending || chinaAndroidTrialMutation.isPending

  return <ReminderView onStartTrial={startTrial} isLoading={isLoading()} />
}

function SubscribedHost() {
  const subscribedView = useStore($subscribedView)
  const [animLoaded, setAnimLoaded] = createSignal(false)
  const isOpen = () => animLoaded() && subscribedView() !== null
  const isTrial = () => subscribedView()?.isChinaAndroidTrial ?? false
  return (
    <SubscribedView
      isOpen={isOpen()}
      isTrial={isTrial()}
      onAnimLoaded={() => setAnimLoaded(true)}
    />
  )
}
